import moment from 'moment'
import { pluralize } from 'utils/misc'
import { DEPLOYMENT_KEYS, DATE_FORMAT_MAP } from 'utils/constants'
import { fromUTCMidnightSeconds } from 'utils/time'
import { normalizeSchedules } from '../../../../Instruments/utils/multiSchedule'
import { getScheduleStartTypeLabel } from '../../../../Instruments/routes/Instrument/routes/InstrumentPreview/utils/schedulePreviewHelpers'
import { resolveScheduleVisibility } from '../../../../Instruments/utils/scheduleVisibility'
import { getChainDeployVisitIds, formatTimeOfDay } from './scheduleProjection'
import { FALLBACKS, SCHEDULE, TIME } from './constants'

export { getScheduleStartTypeLabel }

const formatVisibilityRange = range => {
  const start = formatTimeOfDay(range?.start)
  const end = formatTimeOfDay(range?.end)
  if (!start || !end) return null
  return `${start} - ${end}`
}

/**
 * GOLD-12272: Visible field in the schedule detail panel.
 * Multi-schedule: visibility / visibilityRanges live on each schedule entry.
 * Legacy (multi_schedule off or absent): fields live on instrument metadata.
 */
export const getVisibleFieldLabel = (schedule, metadata) => {
  const { visibility, visibilityRanges } = resolveScheduleVisibility(schedule, metadata)
  if (visibility !== 'specific_time') {
    return TIME.ALL_DAY_VISIBILITY
  }
  const rangeLabels = (visibilityRanges || []).map(formatVisibilityRange).filter(Boolean)
  if (!rangeLabels.length) return TIME.AT_SPECIFIC_TIMES
  return `${TIME.AT_SPECIFIC_TIMES} (${rangeLabels.join(', ')})`
}

const getChainDeployVisitLabel = (chainDeployment, visitIdToName = {}) => {
  const visitIds = getChainDeployVisitIds(chainDeployment)
  if (visitIds.length === 1) {
    const visitId = visitIds[0]
    return (
      visitIdToName[String(visitId)] ||
      chainDeployment.visit_name ||
      chainDeployment.visit_display_name ||
      `${SCHEDULE.VISIT} ${visitId}`
    )
  }
  if (visitIds.length > 1) {
    const names = visitIds.map(id => visitIdToName[String(id)]).filter(Boolean)
    if (names.length) return names.join(', ')
  }
  return chainDeployment.visit_name || chainDeployment.visit_display_name || SCHEDULE.VISIT.toLowerCase()
}

export const getStartLabel = (
  schedule,
  visitIdToName = {},
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
  instrumentData = null,
) => {
  const deploy = schedule?.deploy || {}
  const expire = schedule?.expire || {}
  const chainDeployment = schedule?.chain_deployment_info || {}

  if ('now' in deploy) {
    const deployedAt = instrumentData?.deployed
    const timeOfDay = deploy.time_of_day
    if (deployedAt) {
      try {
        const mom = moment.utc(deployedAt)
        if (mom.isValid()) {
          const dateStr = mom.format(DATE_FORMAT_MAP.main)
          const formattedTime = formatTimeOfDay(timeOfDay)
          return formattedTime ? `${dateStr} (${formattedTime})` : dateStr
        }
      } catch (_) {
        /* fall through */
      }
    }
    return TIME.IMMEDIATELY
  }
  if (deploy.absolute != null) {
    try {
      return fromUTCMidnightSeconds(deploy.absolute).format(DATE_FORMAT_MAP.main)
    } catch {
      return TIME.EXACT_DATE
    }
  }
  if (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeployment) {
    const visitName = getChainDeployVisitLabel(chainDeployment, visitIdToName)
    const daysOffset = deploy.days_offset
    if (daysOffset === 0 || daysOffset == null) return `${SCHEDULE.ON_DAY_OF_VISIT} (${visitName})`
    if (daysOffset > 0) return `${pluralize(daysOffset, 'day', 'days')} ${SCHEDULE.AFTER_VISIT} (${visitName})`
    return `${pluralize(Math.abs(daysOffset), 'day', 'days')} ${SCHEDULE.BEFORE_VISIT} (${visitName})`
  }
  if (DEPLOYMENT_KEYS.chainDeployOffset in deploy) {
    const n = deploy.chain_deploy_offset
    return n ? `${pluralize(n, 'day', 'days')} ${SCHEDULE.AFTER_TRIGGER}` : TIME.IMMEDIATELY_AFTER_TRIGGER
  }
  if (deploy.first_login) {
    const n = deploy.first_login.interval
    return `${pluralize(n, 'Day', 'Days')} ${SCHEDULE.DAYS_AFTER_FIRST_LOGIN}`
  }
  if (expire.relative_to_send_ideal && !deploy.first_login) {
    if (useAbsoluteStartDate && absoluteStartDate) {
      const mom = moment(absoluteStartDate)
      return mom.isValid() ? mom.format(DATE_FORMAT_MAP.main) : TIME.CURRENT_DATE
    }
    return TIME.CURRENT_DATE
  }
  return ''
}

/** Map instrument id → Set of scheduleIndex values projected onto the selected day. */
export const getActiveScheduleIndicesByInstrumentId = (dayInstruments = []) => {
  const map = new Map()
  dayInstruments.forEach(item => {
    const id = String(item.instrumentId ?? item.instrumentData?.id ?? item.id ?? '')
    if (!id) return
    const idx = item.scheduleIndex
    if (typeof idx !== 'number' || idx < 0) return
    if (!map.has(id)) map.set(id, new Set())
    map.get(id).add(idx)
  })
  return map
}

const getInstrumentId = item =>
  String(item?.instrumentId ?? item?.instrumentData?.id ?? item?.id ?? '')

const getOccurrenceScheduleIndex = item =>
  typeof item?.scheduleIndex === 'number' && item.scheduleIndex >= 0 ? item.scheduleIndex : 0

/**
 * One sidebar card per instrument on the selected day.
 * All of that instrument's schedules are listed inside the drawer.
 */
export const getUniqueInstrumentsForDay = (dayInstruments = []) => {
  const seen = new Set()
  const list = []
  dayInstruments.forEach(instrument => {
    const id = getInstrumentId(instrument)
    if (!id || seen.has(id)) return
    seen.add(id)
    list.push(instrument)
  })
  return list
}

/** Visible timeline/drawer label; includes Schedule N when the instrument has multiple schedules. */
export const getInstrumentOccurrenceLabel = (item, fallbackName = FALLBACKS.EMPTY_DASH) => {
  const instrumentData = item?.instrumentData || {}
  const name =
    item?.label ||
    instrumentData.metadata?.display_name ||
    instrumentData.title ||
    instrumentData.name ||
    fallbackName
  const schedules = normalizeSchedules(instrumentData.metadata?.schedule)
  if (schedules.length < 2) return name
  return `${name} (Schedule ${getOccurrenceScheduleIndex(item) + 1})`
}

/** Collapsed drawer subtitle: per-schedule visibility when set, otherwise the deploy window. */
export const getInstrumentScheduleDrawerSubtitle = item => {
  const instrumentData = item?.instrumentData || {}
  const schedules = normalizeSchedules(instrumentData.metadata?.schedule)
  const schedule = schedules[getOccurrenceScheduleIndex(item)] || {}
  const visibleLabel = getVisibleFieldLabel(schedule, instrumentData.metadata)
  if (visibleLabel && visibleLabel !== TIME.ALL_DAY_VISIBILITY) {
    return visibleLabel
  }
  return item?.window || ''
}

export const buildInstrumentOccurrenceTooltip = (
  item,
  { visitIdToName = {}, useAbsoluteStartDate = false, absoluteStartDate = null } = {},
) => {
  if (!item) return ''
  const instrumentData = item.instrumentData || {}
  const schedules = normalizeSchedules(instrumentData.metadata?.schedule)
  const scheduleIndex = typeof item.scheduleIndex === 'number' ? item.scheduleIndex : 0
  const schedule = schedules[scheduleIndex] || schedules[0] || {}
  const name =
    item.label ||
    instrumentData.metadata?.display_name ||
    instrumentData.title ||
    instrumentData.name ||
    'Instrument'
  const lines = [name]
  if (schedules.length > 1) {
    lines.push(`Schedule ${scheduleIndex + 1} · ${getScheduleStartTypeLabel(schedule)}`)
  } else {
    lines.push(getScheduleStartTypeLabel(schedule))
  }
  const start = getStartLabel(
    schedule,
    visitIdToName,
    useAbsoluteStartDate,
    absoluteStartDate,
    instrumentData,
  )
  if (start) lines.push(`Start: ${start}`)
  if (item.window) lines.push(`Time: ${item.window}`)
  return lines.join('\n')
}

export const buildDayInstrumentBarTooltip = (
  instruments = [],
  { visitIdToName = {}, useAbsoluteStartDate = false, absoluteStartDate = null } = {},
) => {
  if (!instruments?.length) return ''
  const options = { visitIdToName, useAbsoluteStartDate, absoluteStartDate }
  return instruments
    .map(item => buildInstrumentOccurrenceTooltip(item, options))
    .filter(Boolean)
    .join('\n---\n')
}
